import React from 'react';
import Icon from '../AppIcon';
import Button from './Button';

/**
 * NSherjFh PR 11 — note hard-delete confirm modal.
 *
 * First HARD-delete modal in the umbrella. PRs 2-10 were soft-delete
 * with Restore; notes are ephemeral per Heidi's 28 May call so they
 * delete permanently — no `deleted_at`, no Recycle Bin, no restore.
 * Copy emphasises the permanence ("This cannot be undone") so admins
 * and site managers don't conflate it with the soft-delete behaviour
 * of every other umbrella entity.
 *
 * Co-located with NotesFeed in components/ui/ because the host
 * component itself is a shared UI piece used across six surfaces
 * (site, task, snag, incident, near_miss, client portal) — same
 * folder, same import depth.
 */
const NoteDeleteConfirmModal = ({ isOpen, onClose, onConfirm, isDeleting = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-md">
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-destructive/10 rounded-full flex-shrink-0">
              <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Delete this note?
              </h2>
              <p className="text-sm text-muted-foreground">
                This will permanently delete the note. This cannot be undone.
              </p>
            </div>
          </div>

          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-2">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">No recovery</p>
                <p className="text-sm text-muted-foreground">
                  Notes are not recoverable after deletion — unlike documents, RAMS, snags, and other records which can be restored from a Deleted tab. If the note attached a photo, the photo is also removed.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            iconName={isDeleting ? 'Loader2' : 'Trash2'}
            iconPosition="left"
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoteDeleteConfirmModal;
